import {
  CollisionDetection,
  DroppableContainer,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  IRootWidget,
  ISortableWidgetData,
  TreeNavigatorLogic,
  Widget,
  WidgetRepository,
} from '@framework/engine';
import { useCallback } from 'react';

// Tipo di input della callback di collisionDetection
type collisionDetectionArgs = Parameters<CollisionDetection>[0];

/**
 * Ritorna la lista di collisions. Se non ci sono collisions con il pointer,
 * ritorna le collisions con i rettangoli.
 */
function getIntersections(
  root: IRootWidget,
  collisionDetection: collisionDetectionArgs,
) {
  // Filtro i widget che sono parent validi
  const validCollisions = collisionDetection.droppableContainers.filter(
    droppable => validDroppableFilter(root, collisionDetection, droppable),
  );

  // First, let's see if there are any collisions with the pointer
  const pointerCollisions = pointerWithin({
    ...collisionDetection,
    droppableContainers: validCollisions,
  });

  // Collision detection algorithms return an array of collisions
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  // If there are no collisions with the pointer, return rectangle intersections
  return rectIntersection({
    ...collisionDetection,
    droppableContainers: validCollisions,
  });
}

function isInsideOrSame(root: IRootWidget, over: Widget, activeId: string) {
  const active = TreeNavigatorLogic.byId(root, activeId);
  if (!active) return false;
  return TreeNavigatorLogic.isInsideOrSame(over, active);
}

/**
 * Versione personalizzata dell'algoritmo di collisione.
 * Utilizza di default `pointerWithin` che è molto più preciso
 *
 * https://docs.dndkit.com/api-documentation/context-provider/collision-detection-algorithms#rectangle-intersection
 */
export function useDndEditorCollisionDetection(root: IRootWidget) {
  return useCallback<CollisionDetection>(
    args => {
      // Collision detection algorithms return an array of collisions
      const intersections = getIntersections(root, args);

      const overWidget = getFirstCollision(intersections);
      const overId = overWidget?.id.toString();

      if (!overId) return [];

      const overWidgetData = overWidget?.data?.droppableContainer.data
        .current as ISortableWidgetData;

      /**
       * Se si tratta di un droppable extra dei container (che non sia il suo), ritorno direttamente
       * il widget su cui si trova il pointer
       */
      if (
        overWidgetData.directionalDroppableType &&
        !!overWidgetData.originalId
      ) {
        // Ignoro il droppable del widget e dei suoi figli per evitare loop
        const over = TreeNavigatorLogic.byId(root, overWidgetData.originalId);
        if (!over || isInsideOrSame(root, over, args.active.id.toString())) {
          return [];
        }
        return [{ id: overId }];
      }

      if (!root) return [];

      const over = TreeNavigatorLogic.byId(root, overId);

      if (!over || isInsideOrSame(root, over, args.active.id.toString())) {
        return [];
      }

      return [{ id: overId }];
    },
    [root],
  );
}

/**
 * Funzione utilizzata come filtro per rimuovere i droppable
 * non validi in base all'active attuale.
 */
function validDroppableFilter(
  root: IRootWidget,
  collisionDetection: collisionDetectionArgs,
  droppable: DroppableContainer,
) {
  // Recupero i dati dal collisionDetection
  const droppableData = droppable.data.current as ISortableWidgetData;
  const activeData = collisionDetection.active.data
    .current as ISortableWidgetData;

  // Recupero il tipo dal repository
  const droppableType = WidgetRepository.find(droppableData.type);
  const activeType = WidgetRepository.find(activeData.type);

  // Il tipo deve essere per forza registrato, altrimenti qualcosa non è valido.
  if (!droppableType) {
    console.warn(`[CollisionDetection] Widget "${droppableData.type}" not found`); // prettier-ignore
    return false;
  }

  if (!activeType) {
    console.warn(`[CollisionDetection] Widget "${droppableData.type}" not found`); // prettier-ignore
    return false;
  }

  // Se non ci sono funzioni di validazione da calcolare, di default ritorno true per evitare computazioni inutili
  if (!activeType.isValidParent && !droppableType.isValidChild) {
    return true;
  }

  // Recupero i widget dal tree
  const activeWidget = TreeNavigatorLogic.byId(
    root,
    collisionDetection.active.id as string,
  );

  const droppableWidget = TreeNavigatorLogic.byId(
    root,
    droppableData.originalId ?? (droppable.id as string),
  );

  // Se non trovo i widget, ritorno false (qualcosa è andato storto)
  if (!activeWidget || !droppableWidget) {
    console.warn(`[CollisionDetection] Widget not found`, {
      activeWidget,
      droppableWidget,
    });
    return false;
  }

  // Gestisco il caso in cui isValid<..> sia "undefined" e quindi true di default.
  const isValidParent = activeType.isValidParent
    ? activeType.isValidParent(activeWidget, droppableWidget, droppableData)
    : true;
  const isValidChild = droppableType.isValidChild
    ? droppableType.isValidChild(droppableWidget, activeWidget)
    : true;

  // La collisione è da considerarsi soltanto se sono validi sia il parent che il child
  return isValidParent && isValidChild;
}
